import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import DiaCircle from '../DiaCircle';
import GaugeChip from '../GaugeChip';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
  count: number;
  created_at: string;
  updated_at: string;
}

const MachineList = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const { translations: t } = useTranslation();

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (!response.ok) {
        throw new Error('Failed to fetch machines');
      }

      const data = await response.json();
      // Sort machines by gauge (ascending), then by dia (ascending)
      const sortedData = data.sort((a: Machine, b: Machine) => {
        const gaugeA = parseInt(a.gauge) || 0;
        const gaugeB = parseInt(b.gauge) || 0;
        if (gaugeA !== gaugeB) {
          return gaugeA - gaugeB;
        }
        return a.dia - b.dia;
      });
      setMachines(sortedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/machines/new');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            mb: { xs: 2, sm: 3 }, 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
          }}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleCreateNew}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
            >
              {t.machines.createNew}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t.machines.dia}</TableCell>
                  <TableCell>{t.machines.gauge}</TableCell>
                  <TableCell>{t.machines.machineType}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Count</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t.date}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {machines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t.machines.noMachines}
                    </TableCell>
                  </TableRow>
                ) : (
                  machines.map((machine) => (
                    <TableRow 
                      key={machine.id}
                      onClick={() => navigate(`/machines/${machine.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <DiaCircle
                            dia={machine.dia}
                            gauge={machine.gauge}
                            machineType={machine.machine_type}
                            machines={machines}
                            size="small"
                          />
                          <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(machine.created_at).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="primary">
                              Count: {machine.count}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <GaugeChip gauge={machine.gauge} />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={machine.machine_type} 
                          size="small"
                          sx={{
                            bgcolor: machine.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                            color: machine.machine_type === 'rib' ? '#000' : '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip 
                          label={machine.count} 
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {new Date(machine.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default MachineList;
